/**
 * Import the necessary dependencies from the React and react-i18next libraries.
 * @module React
 * @module useTranslation
 */
import React from "react";
import { useTranslation } from "react-i18next";

/**
 * Import statements for various components and utilities.
 * @import ConfirmModal from "../../components/confirmModal/confirmModel"
 * @import CustomButton from "../../components/customButton/customButton"
 * @import TextField from "../../components/TextField/TextField"
 * @import HookForm from "../../components/HookForm/HookForm"
 * @import Constant from "../../util/constant"
 */
import ConfirmModal from "../../components/confirmModal/confirmModel";
import CustomButton from "../../components/customButton/customButton";
import TextField from "../../components/textField/textField";
import HookForm from "../../components/hookForm/hookForm";
import Constant from "../../util/constant";

/**
 * A functional component that renders a modal for updating a depot.
 * @param {object} props - The component props.
 * @param {boolean} props.showUpdateDepotModal - Flag indicating whether the update depot modal should be shown.
 * @param {function} props.closeUpdateDepotModal - Function to close the update depot modal.
 * @param {function} props.updateDepotFun - Function to update the depot.
 * @param {function} props.updateDepotFunction - Function to handle the update depot form.
 * @param {function} props.clearUpdateData - Function to clear the update data.
 * @returns The rendered update depot modal.
 */
const UpdateDepot = (props) => {
  const { t } = useTranslation();

  /**
   * Destructures the necessary props from the given object.
   * @param {object} props - The object containing the props.
   * @returns An object containing the destructured props.
   */

  const {
    showUpdateDepotModal,
    closeUpdateDepotModal,
    updateDepotFun,
    updateDepotFunction,
    clearUpdateData,
    handleUpdateFormSubmit,
    updateDepotModelLoaderButton,
    updateDepotsValue,
    issetupdateDepotValue,
    updatetextcount,
    handleupdatetextChange
  } = props;


  /**
   * An object representing the update form for the depot name in the depot form.
   * @property {string} name - The name of the depot field.
   * @property {object} validate - The validation rules for the depot name field.
   * @property {object} validate.required - The required validation rule for the depot name field.
   * @property {boolean} validate.required.value - Indicates if the depot name field is required.
   * @property {object} validate.pattern - The pattern validation rule for the depot name field.
   * @property {string} validate.pattern.value - The regular expression pattern for the depot name field.
   * @property {string} validate.pattern.message - The error message to display if the pattern validation fails.
   * @property
   */
  const UpdateFormDepot = {
    UpdateDepotname: {
      name: "Depotname",
      validate: {
        required: {
          value: true,
        },
        pattern: {
          value: Constant.REGEX.NAME,
          message: t("ALLOWED_ONLY_PERIOD_APOSTROPHE_HYPHEN_DASH_AND_SPACES"),
        },
        maxLength: {
          value: 200,
          message: t("ALLOWED_MAX_200_CHARACTERS"),
        },
      },
    },
  };

  return (
    /**
     * A modal component used for updating a depot.
     * @param {string} className - The class name for the modal.
     * @param {string} modalTitle - The title of the modal.
     * @param {boolean} showModal - Whether or not to show the modal.
     * @param {boolean} isModelIcon - Whether or not to show the model icon.
     * @param {boolean} modalContent - The content of the modal.
     * @param {boolean} isFirstButton - Whether or not to show the first button.
     * @param {boolean} isSecondButton - Whether or not to show the second button.
     * @param {function} onProcced - The function to execute when proceeding.
     */
    <ConfirmModal
      className={"Deactivate import-model"}
      modalTitle={t("EDIT_DEPOT")}
      showModal={showUpdateDepotModal}
      isModelIcon={false}
      modalContent={false}
      isFirstButton={false}
      isSecondButton={false}
      onProcced={() => { }}
      closeModelIcon={() => closeUpdateDepotModal()}
      isCloseBtn={true}
    >
      <HookForm
        className="form-horizontal"
        defaultForm={{
          Depotname: "",
        }}
        init={(form) => {

          updateDepotFunction(form);
        }}
        onSubmit={(e) => {
          handleUpdateFormSubmit(e)
        }}
      >
        {(formMethod) => {
          return (
            <>
              <div className="uploadWrapper CreateDepotWrapper">
                <div className="d-flex">
                  <p className="form-label">{"Depot Name"}</p>
                  <p className="requiredStyle">*</p>
                </div>
                <div className="TextFiledWrapper">
                  <TextField
                    formMethod={formMethod}
                    rules={UpdateFormDepot.UpdateDepotname.validate}
                    name={UpdateFormDepot.UpdateDepotname.name}
                    errors={formMethod?.formState.errors}
                    value={updateDepotsValue}
                    autoFocus={true}
                    textCountVisible={true}
                    textcount={updatetextcount}
                    onChange={handleupdatetextChange}
                    textCountTotal={200}
                    type="text"
          


                    placeholder={t("ENTER_DEPOT_NAME")}
                  />
                  {formMethod.getValues("Depotname") !== "" ? (
                    <button
                      onClick={() => {
                        clearUpdateData();
                      }}
                      className="ClearInput btn-close"
                      aria-label="Close"
                    />
                  ) : (
                    ""
                  )}
                </div>
              </div>
              <div className="Form_buton">
                <CustomButton
                  className="black-btn Fill_Button"
                  disabled={
                    (updateDepotsValue === issetupdateDepotValue && true) ||
                    !formMethod?.formState?.isDirty
                  }
                  onClick={updateDepotFun}
                  loading={updateDepotModelLoaderButton}
                >
                  {t("UPDATE")}
                </CustomButton>
                <CustomButton
                  type="button"
                  className="black-btn Border_Button"
                  onClick={() => closeUpdateDepotModal(true)}
                >
                  {t("CANCEL")}
                </CustomButton>
              </div>
            </>
          );
        }}
      </HookForm>
    </ConfirmModal>
  );
};

export default UpdateDepot;
